import {Modal, Tag, Tooltip} from 'antd';
import {useSelector} from 'react-redux';
import {makeSelectTabOperations} from 'redux/store/auth';
import {NavTabsEnum} from 'utils/constants';
import {
  KYC_REJECTION_REASONS,
  resolveReasonSelection,
} from 'utils/rejectionReasonRegistry';
import UserLink, {userHref} from 'components/userLink/UserLink';

/** Where a queue row's user opens: their KYC tab, the same place Review goes. */
export const kycTabHref = userId => userHref(userId, 'kyc-status');

/**
 * A queue row's name or email, opening that user's KYC tab in a new tab, like
 * Review, so the reviewer's place in the queue survives the click.
 */
export const KycUserLink = ({user, children}) => (
  <UserLink userId={user?.id} tab="kyc-status">
    {children}
  </UserLink>
);

// Matches the server default. The server caps anything above 100.
export const DEFAULT_PAGE_SIZE = 20;

export const PAGE_SIZE_OPTIONS = ['20', '50', '100'];

// Turn an enum like "NATIONAL_ID" into a readable "National Id".
export const humanizeEnum = value =>
  value
    ? String(value)
        .toLowerCase()
        .split('_')
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : '-';

// Every InvestorKycStatus value. The identity, AML and document checks each
// carry one of these, independently of the other two, so all three columns are
// described from this one map.
export const KYC_STATUS_TAG = {
  PENDING_VERIFICATION: {color: 'gold', label: 'Awaiting review'},
  APPROVED: {color: 'green', label: 'Approved'},
  // Its own label, deliberately: it clears the same gates as Approved, and an
  // operator still needs to see that the approval came with a condition.
  CONDITIONALLY_APPROVED: {color: 'cyan', label: 'Conditionally approved'},
  REJECTED: {color: 'red', label: 'Rejected'},
  PENDING: {color: 'default', label: 'Pending'},
  NOT_STARTED: {color: 'default', label: 'Not started'},
};

// A card is never removed when it is decided — it is filed under its new KYC
// status. Filtering by status is how you get at the decided history.
export const KYC_STATUS_OPTIONS = [
  {value: 'PENDING_VERIFICATION', label: 'Awaiting review'},
  {value: 'APPROVED', label: 'Approved'},
  // Filters match the selected statuses exactly, so this is a separate option
  // rather than something Approved quietly widens to. Pick both for either.
  {value: 'CONDITIONALLY_APPROVED', label: 'Conditionally approved'},
  {value: 'REJECTED', label: 'Rejected'},
  {value: 'PENDING', label: 'Pending'},
  {value: 'NOT_STARTED', label: 'Not started'},
];

/** The words on the tag, for anywhere a tag will not do — a CSV cell. */
export const kycStatusLabel = status =>
  KYC_STATUS_TAG[status]?.label || humanizeEnum(status);

export const renderKycStatusTag = status => {
  const tag = KYC_STATUS_TAG[status];
  return tag ? <Tag color={tag.color}>{tag.label}</Tag> : humanizeEnum(status);
};

/**
 * Confirms an export before the file is written.
 *
 * The count is the point of it: the button sits under a filtered, paginated
 * table, and "export" reads like the whole queue when it is only the page in
 * front of you. Saying the number out loud is what catches that.
 *
 * Shaped for react-csv's async click — `done(false)` cancels the download.
 */
export const confirmQueueExport = count => (event, done) =>
  Modal.confirm({
    title: 'Export this page?',
    content: `${count} ${
      count === 1 ? 'user' : 'users'
    } will be written to a CSV file — the rows on this page, not the whole filtered queue.`,
    okText: 'Export',
    cancelText: 'Cancel',
    centered: true,
    onOk: () => done(true),
    onCancel: () => done(false),
  });

/**
 * True when this admin may export the KYC queues.
 *
 * EXPORT is its own operation on the KYC Dashboard grant, so a reviewer can be
 * given the queues without being given the ability to pull a file of names and
 * emails off them. SUPERADMIN and ADMIN hold full access and always may.
 */
export const useCanExportKyc = () => {
  const operations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.KycDashboard),
  );
  return Boolean(operations?.export);
};

/**
 * The rows on screen, as CSV.
 *
 * Built from the table's own column list so the two cannot drift: a column
 * carries an `exportValue` beside its `render`, and the header is the column
 * title the reviewer is looking at. Columns without one — the action column —
 * are left out.
 *
 * It exports the page in front of you, not the whole filtered queue: there is
 * no server-side export yet, and the browser only ever has this page.
 *
 * ponytail: current page only, swap for a download mutation when the backend
 * grows one.
 */
export const buildQueueCsv = (columns, rows = []) => {
  const exportable = columns.filter(column => column.exportValue);

  return {
    headers: exportable.map(column => ({label: column.title, key: column.key})),
    data: rows.map(record =>
      Object.fromEntries(
        exportable.map(column => [
          column.key,
          // A blank cell rather than "undefined" in the file.
          column.exportValue(record) ?? '',
        ]),
      ),
    ),
  };
};

// The code a provider sends back is not always one of ours — ComplyCube and
// Smile ID have their own. Fall back to showing the raw code rather than
// hiding a rejection reason the registry happens not to know.
export const describeRejection = (code, messages) => {
  if (!code && !messages) return null;
  const resolved = resolveReasonSelection({
    code,
    messages,
    registry: KYC_REJECTION_REASONS,
  });
  const known = KYC_REJECTION_REASONS.find(reason => reason.code === code);
  return [known?.label || code, resolved.messageEN].filter(Boolean).join(' — ');
};

// Wraps a status tag in the rejection reason, when there is one to show.
export const renderKycStatusTagWithReason = (status, code, messages) => {
  const tag = renderKycStatusTag(status);
  if (status !== 'REJECTED') return tag;
  const reason = describeRejection(code, messages);
  return reason ? (
    <Tooltip title={reason}>
      <span>{tag}</span>
    </Tooltip>
  ) : (
    tag
  );
};
